"use client";

import type { FC } from "react";
import { cn } from "utils";
import { EdHeroBubble } from "../quiz/EdHeroBubble";
import { QuizChoiceOption } from "../quiz/QuizChoiceOption";
import { QuizProgressHeader, QuizStepLayout } from "../quiz/QuizStepLayout";
import { QUIZ_COMMON_COPY } from "../onboardingWizardCopy";
import type { QuizStepDefinition } from "../quizStepsConfig";
import { resolveQuizHeroMessage } from "../quizStepsConfig";

interface ChoiceQuizStepProps {
  definition: QuizStepDefinition;
  value: string;
  displayName: string;
  progressIndex: number;
  progressTotal: number;
  onBack: () => void;
  onChange: (value: string) => void;
  onContinue: (selectedValue: string) => void;
}

export const ChoiceQuizStep: FC<ChoiceQuizStepProps> = ({
  definition,
  value,
  displayName,
  progressIndex,
  progressTotal,
  onBack,
  onChange,
  onContinue,
}) => {
  const options = definition.options ?? [];

  return (
    <QuizStepLayout
      testId={`onboarding-choice-quiz-${definition.stepId}`}
      header={
        <QuizProgressHeader
          progressIndex={progressIndex}
          progressTotal={progressTotal}
          onBack={onBack}
        />
      }
    >
      <div
        className="flex flex-1 flex-col gap-8 overflow-y-auto px-2 pb-7 pt-6 md:gap-[60px] md:px-0 md:pb-0 md:pt-0"
        data-testid="choice-quiz-body"
        data-figma-node={
          value
            ? (definition.figmaNodeIds[1] ?? definition.figmaNodeIds[0])
            : definition.figmaNodeIds[0]
        }
      >
        <EdHeroBubble
          message={resolveQuizHeroMessage(
            definition,
            displayName,
            QUIZ_COMMON_COPY.greetingTemplate,
          )}
        />

        <div
          className={cn(
            "flex flex-col gap-6",
            definition.stepId === "quiz/risk" && "md:gap-8",
          )}
          data-testid="choice-quiz-question-block"
        >
          <h2
            className="headline_regular_m text-content_inverse_primary"
            data-testid="choice-quiz-question"
          >
            {definition.question}
          </h2>
          <div
            className="flex flex-col gap-3"
            data-testid="choice-quiz-options"
          >
            {options.map((option) => (
              <QuizChoiceOption
                key={option.id}
                optionId={option.id}
                label={option.label}
                selected={value === option.id}
                onSelect={() => {
                  onChange(option.id);
                  onContinue(option.id);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </QuizStepLayout>
  );
};
